import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dna, Calendar, Bell, FileText, BookOpen, FlaskConical, Brain, Timer, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import { useNotices } from "@/hooks/useNotices";
import { Skeleton } from "@/components/ui/skeleton";

const resourceCategories = [
  { id: "general", name: "일반 자료", nameEn: "General Resources", icon: BookOpen, color: "bg-jax-blue" },
  { id: "cancer", name: "암 연구 모델", nameEn: "Cancer Research", icon: FlaskConical, color: "bg-rose-500" },
  { id: "aging", name: "노화 연구 모델", nameEn: "Aging Research", icon: Timer, color: "bg-amber-500" },
  { id: "immunodeficient", name: "면역결핍 모델", nameEn: "Immunodeficient Models", icon: Shield, color: "bg-emerald-500" },
  { id: "neurodegenerative", name: "신경퇴행성 질환 모델", nameEn: "Neurodegenerative Diseases", icon: Brain, color: "bg-purple-500" },
];

const TwoColumns = () => {
  const { data: notices = [], isLoading, error } = useNotices(5);

  return (
    <section className="section-padding-sm bg-gradient-to-br from-background via-background/50 to-primary/5">
      <div className="container max-w-7xl mx-auto container-padding">
        {/* 2열 레이아웃 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:items-stretch">
          {/* 왼쪽: 공지사항 */}
          <Card className="border-0 shadow-clean overflow-hidden h-full flex flex-col">
            <CardContent className="p-0 flex flex-col h-full">
              {/* 게시판 헤더 */}
              <div className="bg-jax-blue flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-bold text-white">공지사항</h2>
                </div>
                <Link
                  to="/notice" 
                  aria-label="공지사항 더보기"
                  className="text-xs text-white/90 hover:text-white flex items-center gap-1 transition-colors"
                >
                  더보기
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              
              {/* 게시판 테이블 */}
              <div className="bg-white flex-1">
                {isLoading ? (
                  <div className="divide-y divide-border/50">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <div key={index} className="flex items-center gap-4 px-4 py-3">
                        <Skeleton className="h-5 w-12 flex-shrink-0" />
                        <Skeleton className="h-4 flex-1" />
                        <Skeleton className="h-4 w-20 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="text-center py-6">
                    <p className="text-muted-foreground text-sm">공지사항을 불러올 수 없습니다.</p>
                  </div>
                ) : notices.length === 0 ? (
                  <div className="text-center py-6">
                    <p className="text-muted-foreground text-sm">현재 공지사항이 없습니다.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {notices.map((notice) => {
                      const previewText = notice.content.replace(/<[^>]*>/g, '').substring(0, 60);
                      return (
                        <Link
                          key={notice.id}
                          to={`/notice/${notice.id}`}
                          className="flex items-center gap-4 px-4 py-3 hover:bg-muted/30 transition-colors group"
                        >
                          {/* 뱃지 */}
                          <div className="flex-shrink-0 w-12">
                            {notice.priority >= 3 ? (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0">긴급</Badge>
                            ) : notice.priority === 2 ? (
                              <Badge variant="default" className="text-[10px] px-1.5 py-0">중요</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">일반</Badge>
                            )}
                          </div>
                          
                          {/* 제목 + 미리보기 */}
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm text-foreground truncate group-hover:text-jax-blue transition-colors">
                              {notice.title}
                            </h3>
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {previewText}...
                            </p>
                          </div>
                          
                          {/* 날짜 */}
                          <div className="flex-shrink-0 text-xs text-muted-foreground hidden sm:flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(notice.created_at).toLocaleDateString('ko-KR')}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 오른쪽: 연구자료 + 동물 모델 통합 */}
          <Card className="border-0 shadow-clean overflow-hidden h-full flex flex-col">
            <CardContent className="p-0 flex flex-col h-full">
              {/* 연구자료 헤더 */}
              <div className="bg-jax-blue flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-bold text-white">연구자료</h2>
                </div>
                <Link
                  to="/resources" 
                  aria-label="연구자료 더보기"
                  className="text-xs text-white/90 hover:text-white flex items-center gap-1 transition-colors"
                >
                  더보기
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className="card-padding bg-white flex flex-col">
                {/* 연구자료 카테고리 */}
                <div className="grid grid-cols-2 gap-2">
                  {resourceCategories.map((category) => {
                    const IconComponent = category.icon;
                    return (
                      <Link
                        key={category.id}
                        to={`/resources#resource-${category.id}`}
                        className="flex items-center gap-2 p-2 rounded-lg border border-border/50 hover:border-jax-blue/30 hover:shadow-sm transition-all group/item text-left"
                      >
                        <div className={`w-7 h-7 ${category.color} rounded-lg flex items-center justify-center shadow-sm flex-shrink-0`}>
                          <IconComponent className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-foreground group-hover/item:text-jax-blue transition-colors truncate">
                            {category.name}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>

              {/* 동물 모델 헤더 */}
              <div className="bg-jax-blue flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <Dna className="w-5 h-5 text-white" />
                  <h2 className="text-lg font-bold text-white">주요 동물 모델</h2>
                </div>
                <Link
                  to="/animal-list" 
                  aria-label="모든 동물 모델 더보기"
                  className="text-xs text-white/90 hover:text-white flex items-center gap-1 transition-colors"
                >
                  더보기
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
              <div className="card-padding bg-white flex-1">
                <div className="space-y-3">
                  {/* Cancer Models */}
                  <div className="border-l-3 border-rose-400 pl-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1 bg-rose-100 text-rose-700">
                        <FlaskConical className="w-2.5 h-2.5" />
                        Cancer
                      </Badge>
                      <a href="https://www.jax.org/strain/001303" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-foreground hover:text-jax-blue transition-colors">
                        NOD/SCID
                      </a>
                      <span className="text-[10px] text-muted-foreground">JAX® 001303</span>
                    </div>
                  </div>

                  {/* Neurological Models */}
                  <div className="border-l-3 border-purple-400 pl-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1 bg-purple-100 text-purple-700">
                        <Brain className="w-2.5 h-2.5" />
                        Neuro
                      </Badge>
                      <a href="https://www.jax.org/strain/000664" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-foreground hover:text-jax-blue transition-colors">
                        C57BL/6J
                      </a>
                      <span className="text-[10px] text-muted-foreground">JAX® 000664</span>
                    </div>
                  </div>

                  {/* Aging Models */}
                  <div className="border-l-3 border-amber-400 pl-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1 bg-amber-100 text-amber-700">
                        <Timer className="w-2.5 h-2.5" />
                        Aging
                      </Badge>
                      <a href="https://www.jax.org/strain/000664" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-foreground hover:text-jax-blue transition-colors">
                        Aged C57BL/6J
                      </a>
                      <span className="text-[10px] text-muted-foreground">JAX® 000664</span>
                    </div>
                  </div>

                  {/* Immunodeficient Models */}
                  <div className="border-l-3 border-emerald-400 pl-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex items-center gap-1 bg-emerald-100 text-emerald-700">
                        <Shield className="w-2.5 h-2.5" />
                        Immuno
                      </Badge>
                      <a href="https://www.jax.org/strain/005557" target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-foreground hover:text-jax-blue transition-colors">
                        NSG
                      </a>
                      <span className="text-[10px] text-muted-foreground">JAX® 005557</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default TwoColumns;
